import React, { useContext, useMemo, useState } from "react";
import { CartContext } from "./CartContext";
import "../css/checkout.css";
import productData from "../Datas/productData";
import { Link } from "react-router-dom";
import { ALCOHOL_NOTICE, formatMoney, SHIPPING_COUNTRIES, SITE_CONFIG } from "../config/siteConfig";
import { buildPaymentPath, createOrderNumber } from "../utils/payment";

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CheckOut = () => {
  const { cartItems } = useContext(CartContext);
  const [form, setForm] = useState({
    email: "",
    country: "HK",
    firstName: "",
    lastName: "",
    address: "",
    address2: "",
    city: "",
    postalCode: "",
    ageConfirmed: false,
  });
  const [errors, setErrors] = useState({});

  const lines = useMemo(
    () => cartItems.map((item) => ({ ...item, product: productData.find((product) => product.id === item.id) })).filter((item) => item.product),
    [cartItems]
  );
  const subtotal = lines.reduce((sum, item) => sum + Number(item.product.price) * item.quantity, 0);
  const total = subtotal + SITE_CONFIG.shippingFee;
  const containsAlcohol = lines.some((item) => item.product.alcohol);

  const setField = (event) => {
    const { name, value, checked, type } = event.target;
    setForm((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
    setErrors((current) => ({ ...current, [name]: "" }));
  };

  const validate = () => {
    const next = {};
    if (!emailPattern.test(form.email.trim())) next.email = "Enter a valid email address";
    if (!form.country) next.country = "Choose a delivery country or region";
    if (!form.firstName.trim()) next.firstName = "Enter the recipient's first name";
    if (!form.lastName.trim()) next.lastName = "Enter the recipient's last name";
    if (!form.address.trim()) next.address = "Enter the delivery address";
    if (!form.city.trim()) next.city = "Enter the city or district";
    if (!form.postalCode.trim()) next.postalCode = "Enter a postal code, or N/A where none is used";
    if (containsAlcohol && !form.ageConfirmed) next.ageConfirmed = "You must make the age declaration to buy alcohol";
    return next;
  };

  const handlePayNow = (event) => {
    event.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      document.getElementById(Object.keys(nextErrors)[0])?.focus();
      return;
    }

    const origin = window.location.origin;
    const paymentPath = buildPaymentPath({
      orderNo: createOrderNumber(),
      amount: total,
      currency: SITE_CONFIG.currency,
      email: form.email.trim(),
      successUrl: `${origin}/payment?state=success`,
      cancelUrl: `${origin}/payment?state=cancel`,
      title: lines[0].product.name,
    });
    window.location.assign(paymentPath);
  };

  if (lines.length === 0) {
    return (
      <main className="main-containerCheck checkout-empty">
        <h1>Your cart is empty</h1>
        <p>Add a product before opening checkout.</p>
        <Link to="/shop" className="check-checkout-button">Return to shop</Link>
      </main>
    );
  }

  return (
    <main className="main-containerCheck">
      <div className="check-custom-checkout-page">
        <div className="left">
          <div className="check-order-info">
            <form onSubmit={handlePayNow} noValidate>
              <h1>Checkout</h1>
              {Object.keys(errors).length > 0 && (
                <div className="checkout-error-summary" role="alert">Please correct the highlighted fields before continuing.</div>
              )}
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input type="email" id="email" name="email" value={form.email} onChange={setField} autoComplete="email" aria-invalid={Boolean(errors.email)} required />
                {errors.email && <p className="error">{errors.email}</p>}
              </div>
              <div className="form-group">
                <label htmlFor="country">Country / region</label>
                <select id="country" name="country" value={form.country} onChange={setField} autoComplete="country" aria-invalid={Boolean(errors.country)} required>
                  {SHIPPING_COUNTRIES.map((country) => <option key={country.code} value={country.code}>{country.name}</option>)}
                </select>
                {errors.country && <p className="error">{errors.country}</p>}
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First name</label>
                  <input id="firstName" name="firstName" value={form.firstName} onChange={setField} autoComplete="given-name" aria-invalid={Boolean(errors.firstName)} required />
                  {errors.firstName && <p className="error">{errors.firstName}</p>}
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Last name</label>
                  <input id="lastName" name="lastName" value={form.lastName} onChange={setField} autoComplete="family-name" aria-invalid={Boolean(errors.lastName)} required />
                  {errors.lastName && <p className="error">{errors.lastName}</p>}
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="address">Address</label>
                <input id="address" name="address" value={form.address} onChange={setField} autoComplete="address-line1" aria-invalid={Boolean(errors.address)} required />
                {errors.address && <p className="error">{errors.address}</p>}
              </div>
              <div className="form-group">
                <label htmlFor="address2">Apartment, suite or unit (optional)</label>
                <input id="address2" name="address2" value={form.address2} onChange={setField} autoComplete="address-line2" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="city">City / district</label>
                  <input id="city" name="city" value={form.city} onChange={setField} autoComplete="address-level2" aria-invalid={Boolean(errors.city)} required />
                  {errors.city && <p className="error">{errors.city}</p>}
                </div>
                <div className="form-group">
                  <label htmlFor="postalCode">Postal code</label>
                  <input id="postalCode" name="postalCode" value={form.postalCode} onChange={setField} autoComplete="postal-code" aria-invalid={Boolean(errors.postalCode)} required />
                  {errors.postalCode && <p className="error">{errors.postalCode}</p>}
                </div>
              </div>

              <div className="checkout-shipping-method">
                <span>Shipping</span><strong>{formatMoney(SITE_CONFIG.shippingFee)}</strong>
              </div>

              {containsAlcohol && (
                <div className="checkout-age-check">
                  <label htmlFor="ageConfirmed">
                    <input type="checkbox" id="ageConfirmed" name="ageConfirmed" checked={form.ageConfirmed} onChange={setField} aria-invalid={Boolean(errors.ageConfirmed)} />
                    <span>I confirm that I am 18 years of age or older.</span>
                  </label>
                  <p>{ALCOHOL_NOTICE}</p>
                  {errors.ageConfirmed && <p className="error">{errors.ageConfirmed}</p>}
                </div>
              )}

              <div className="checkout-hosted-note">
                <strong>Hosted card payment</strong>
                <p>Pay Now sends you to the hosted checkout. This form never asks for your card number, expiry date or security code.</p>
              </div>
              <button type="submit" className="check-checkout-button">Pay Now · {formatMoney(total)}</button>
              <p><Link to="/shipping">Shipping</Link> · <Link to="/returns">Returns</Link> · <Link to="/privacy">Privacy</Link></p>
            </form>
          </div>
        </div>

        <aside className="right" aria-label="Order summary">
          <div className="check-order-summary">
            <div className="check-order-details">
              {lines.map((item) => (
                <div key={`${item.id}-${item.size}-${item.color}`} className="check-cart-item">
                  <img src={item.product.images[0]} alt={item.product.name} />
                  <div className="pro--qty"><div className="qty-inner">{item.quantity}</div></div>
                  <div className="check-item-details">
                    <h3>{item.product.name}</h3>
                    <p>{item.color}{item.size && item.size !== "One size" ? ` · ${item.size}` : ""}</p>
                  </div>
                  <p className="cal-single">{formatMoney(item.product.price * item.quantity)}</p>
                </div>
              ))}
              <div className="check-summary-details">
                <p><span>Merchandise</span><strong>{formatMoney(subtotal)}</strong></p>
                <p><span>Shipping</span><strong>{formatMoney(SITE_CONFIG.shippingFee)}</strong></p>
                <p className="checkout-total"><span>Total</span><strong>{formatMoney(total)}</strong></p>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
};

export default CheckOut;
